/**
 * How far and how the viewer zooms.
 *
 * OpenLayers resolutions here are base pixels per screen pixel, coarsest
 * first: 1 is the scan's own resolution and magnification = baseMag /
 * resolution. DMV gives OpenLayers either the pyramid's own levels (zoom ends
 * at the scan, 40x on a 40x scan) or none at all (zoom practically never
 * ends); DmvEngine asks for none for every series, since a one-image series'
 * own levels are [1] and could never zoom out to fit. DMV also lets zoom
 * overshoot either end and spring back. These helpers end zoom exactly at
 * MAX_MAGNIFICATION, stop it dead at both ends, take the wheel off OpenLayers
 * (scrollZoom drives it), and let a drag glide or not.
 *
 * Zooming in (the wheel, a pinch, the + button, a typed number) goes on to
 * MAX_MAGNIFICATION. The lens's scale, its ticks and the digit keys end at
 * LENS_MAGNIFICATION, as they always did; past it the lens's indicator sits
 * at the top of the scale and its badge reads the real magnification.
 * The view and drag changes use private OpenLayers fields; the tests pin them
 * against the installed version, and where one is missing DMV's behaviour
 * stays.
 */

import Kinetic from 'ol/Kinetic';

export const MAX_MAGNIFICATION = 300;
export const LENS_MAGNIFICATION = 100;

/**
 * The lens's scale: what its numbers count, where it runs (log-spaced, its
 * ticks), and where zooming in ends, past its top. The engine's magnification
 * is in the scale's unit: baseMag is what resolution 1 reads.
 */
export interface ZoomScale {
  min: number;
  max: number;
  /** Where zooming in ends: past the scale's top, as a slide goes on past 100×. */
  cap: number;
  ticks: readonly number[];
  unit: '×' | '%';
  /** The ticks read "100×", or bare numbers with the unit on the badge and the hover alone. */
  unitOnTicks: boolean;
  /** What the number is called (the typed entry's label). */
  name: 'Magnification' | 'Zoom';
  /**
   * What the image's own resolution reads when the scale fixes it: 100% of an
   * X-ray's pixels. Absent, it is the slide's nearest objective, from its
   * pixel spacing (DmvEngine).
   */
  native?: number;
}

/** A slide's: objective magnification, 1× to 100×, zoom on to 300×. */
export const OBJECTIVE_SCALE: ZoomScale = {
  min: 1,
  max: LENS_MAGNIFICATION,
  cap: MAX_MAGNIFICATION,
  ticks: [1, 2, 5, 10, 20, 40, LENS_MAGNIFICATION],
  unit: '×',
  unitOnTicks: true,
  name: 'Magnification',
};

/**
 * An X-ray's: percent of its own pixels, 100% one to a screen pixel, as
 * radiology viewers say it. An objective's numbers mean nothing for a picture
 * of something centimetres across (decided 2026-10-16).
 */
export const PERCENT_SCALE: ZoomScale = {
  min: 10,
  max: 800,
  cap: 2400,
  ticks: [10, 25, 50, 100, 200, 400, 800],
  unit: '%',
  // Bare numbers on the scale, "%" on the badge and the hover (user, 2026-10-16).
  unitOnTicks: false,
  name: 'Zoom',
  native: 100,
};

/** The value `t` of the way up a scale (0 its bottom, 1 its top), log-spaced: the digit keys' rungs. */
export const scaleAt = (scale: ZoomScale, t: number): number => scale.min * Math.pow(scale.max / scale.min, t);

// Wheel distance per zoom level. OpenLayers uses 300, a third of a level per
// ordinary wheel notch (100); 200 makes a notch half a level.
export const WHEEL_DELTA_PER_ZOOM = 200;

// A wheel notch glides this long to where it lands (scrollZoom). OpenLayers
// glides 250 ms, and on a fast spin cut each glide short to start the next
// from wherever the view had got to; a notch here adds to where the last was
// going, so every notch lands in full however fast they come.
export const WHEEL_GLIDE_MS = 120;
// The buttons, keys, lens and fit: one step each, with a short settle.
export const ZOOM_ANIMATION_MS = 100;

/**
 * The view's resolutions with `minResolution` as the finest, or null when the
 * cap would not leave any zoom range. Keeps the rungs above the cap, continues
 * past the scan in doublings, and ends exactly on the cap, so the last step is
 * never more than 2.5x.
 */
export function magnificationLadder(
  ladder: readonly number[] | undefined,
  maxResolution: number,
  minResolution: number,
): number[] | null {
  if (!(minResolution > 0) || !(maxResolution > minResolution)) return null;
  const out = (ladder?.length ? ladder : [maxResolution]).filter((r) => r > minResolution * (1 + 1e-9));
  if (out.length === 0) out.push(maxResolution);
  let r = out[out.length - 1];
  while (r / 2 > minResolution * 1.25) {
    r /= 2;
    out.push(r);
  }
  out.push(minResolution);
  return out;
}

/**
 * Rebuild a view's zoom range: ending at `cap` (the scale's, MAX_MAGNIFICATION
 * on a slide) when `baseMag` (the magnification at resolution 1) is known, and
 * without the overshoot at either end. It replaces the view's constraints:
 * call it before patching them.
 */
export function limitViewZoom(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  view: any,
  baseMag: number | null | undefined,
  cap: number = MAX_MAGNIFICATION,
): void {
  if (!view?.applyOptions_ || !view.getUpdatedOptions_) return;
  const options: Record<string, unknown> = { smoothResolutionConstraint: false };
  const maxRes = view.getMaxResolution?.();
  if (baseMag && baseMag > 0 && maxRes > 0) {
    const resolutions = magnificationLadder(view.getResolutions?.(), maxRes, baseMag / cap);
    if (resolutions) options.resolutions = resolutions;
  }
  view.applyOptions_(view.getUpdatedOptions_(options));
}

/** Turn off OpenLayers' own wheel zoom: scrollZoom zooms from the wheel
 *  instead, wherever the pointer is. A pinch (Ctrl+wheel) is Viewport's. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function disableWheelZoom(map: any): void {
  for (const interaction of map?.getInteractions?.()?.getArray?.() ?? []) {
    // The wheel zoom is the interaction with a wheel distance per level.
    if (typeof interaction?.deltaPerZoom_ === 'number') interaction.setActive?.(false);
  }
}

/** Whether a drag keeps gliding once the mouse is let go. DMV's map pans with
 *  OpenLayers' default kinetic; without it the slide stops where it is let go. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setPanGlide(map: any, glide: boolean): void {
  for (const interaction of map?.getInteractions?.()?.getArray?.() ?? []) {
    // A drag pan is the interaction with a kinetic switch (OpenLayers' defaults).
    if (interaction && 'noKinetic_' in interaction) {
      interaction.kinetic_ = glide ? new Kinetic(-0.005, 0.05, 100) : undefined;
    }
  }
}

type WheelLike = { deltaY: number; deltaMode: number };

/**
 * A wheel event's vertical travel in pixels. Firefox reports a mouse wheel in
 * lines (deltaMode 1, about 3 a notch), scaled here as OpenLayers' own wheel
 * scales them, so every handler zooms the same in every browser.
 */
export function wheelDeltaPx(e: WheelLike): number {
  return e.deltaY * (e.deltaMode === 1 ? 40 : e.deltaMode === 2 ? 300 : 1);
}

/**
 * Zoom factor for one Ctrl+wheel event: how Chrome, Edge and Firefox deliver a
 * trackpad pinch. Fingers apart (deltaY below 0) zooms in.
 */
export const pinchFactor = (e: WheelLike): number => Math.exp(-wheelDeltaPx(e) * 0.01);

/**
 * The resolution a zoom by `factor` (above 1 zooms in) lands on, held inside
 * the view's zoom range. Set directly, not animated, so it stops dead there.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function zoomedResolution(view: any, res: number, factor: number): number {
  let newRes = res / factor;
  const z = view.getZoomForResolution?.(newRes);
  if (z != null) {
    const min = view.getMinZoom?.() ?? z;
    const max = view.getMaxZoom?.() ?? z;
    newRes = view.getResolutionForZoom?.(Math.min(max, Math.max(min, z))) ?? newRes;
  }
  return newRes;
}

/**
 * Zoom the view to a rung of the ladder (a digit key, the lens, fit): a short
 * glide, or, when something is still animating, straight there. OpenLayers runs
 * every animation it is handed at once rather than replacing the last, so a
 * quick 3-4-5 stacked three glides and loaded tiles for rungs already passed.
 *
 * Returns true when it jumped. Jumping cancels EVERY animation on the view, a
 * rotation or a pan included, so the caller puts those at their targets.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function zoomViewTo(view: any, target: { resolution: number } | { zoom: number }): boolean {
  if (view.getAnimating?.()) {
    view.cancelAnimations();
    if ('zoom' in target) view.setZoom(target.zoom);
    else view.setResolution(target.resolution);
    return true;
  }
  view.animate({ ...target, duration: ZOOM_ANIMATION_MS });
  return false;
}
